import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Loader2 } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/Card';
import Input from '../../components/ui/Input';
import AiCreditsModal, { formatarReais, formatarTokens } from '../../components/admin/AiCreditsModal';
import AiBillingConfigCard from '../../components/admin/AiBillingConfigCard';
import { adminService, AiMonthlyClosing } from '../../services/adminService';

const mesDeHoje = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

/**
 * Fechamento de IA do mês (super-admin): consumo de cada conta para o boleto do pós-pago e
 * acompanhamento do pré-pago. Clicar na conta abre o extrato, a recarga e o pós-pago.
 */
const AdminAiCreditsPage: React.FC = () => {
  const { t } = useTranslation();
  const [mes, setMes] = useState(mesDeHoje());
  const [loading, setLoading] = useState(false);
  const [dados, setDados] = useState<AiMonthlyClosing | null>(null);
  const [conta, setConta] = useState<{ id: string; label: string } | null>(null);

  const carregar = async () => {
    setLoading(true);
    try {
      const res = await adminService.getAiClosing(mes);
      if (res.success && res.data) setDados(res.data);
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('errors.generic'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (/^\d{4}-\d{2}$/.test(mes)) carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mes]);

  const inicio = dados ? new Date(dados.billing_start) : null;

  return (
    <div className="space-y-3 sm:space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{t('admin.aiCreditsTitle', 'Créditos de IA')}</h1>
        <p className="text-gray-600 dark:text-gray-400">
          {t('admin.aiClosingSubtitle', 'Consumo de IA por conta no mês, com a margem da DevSkin. Pós-paga: o valor do mês vai no boleto.')}
        </p>
        {inicio && inicio.getTime() > Date.now() && (
          <p className="mt-2 text-xs rounded-lg bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300 px-3 py-2 inline-block">
            {t('admin.aiBillingShadow', 'A cobrança começa em {{data}}. Até lá o consumo só é registrado: nada é descontado nem bloqueado.', { data: inicio.toLocaleDateString('pt-BR') })}
          </p>
        )}
      </div>

      <Card>
        <CardContent className="p-4 space-y-4">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div className="w-48">
              <Input label={t('admin.aiMonth', 'Mês')} type="month" value={mes} onChange={(e) => setMes(e.target.value)} />
            </div>
            {dados && (
              <p className="text-sm text-gray-700 dark:text-gray-300">
                {t('admin.aiClosingTotal', 'Total do mês')}: <span className="font-bold">{formatarReais(dados.total_cents)}</span>
              </p>
            )}
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
          ) : !dados || dados.accounts.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">{t('common.noData')}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                    <th className="py-2 pr-3">{t('admin.aiAccount', 'Conta')}</th>
                    <th className="py-2 pr-3">{t('admin.aiAccountType', 'Tipo')}</th>
                    <th className="py-2 pr-3 text-right">{t('admin.aiCalls', 'Chamadas')}</th>
                    <th className="py-2 pr-3 text-right">{t('admin.aiTokens', 'Tokens')}</th>
                    <th className="py-2 pr-3 text-right">{t('admin.aiValue', 'Valor')}</th>
                    <th className="py-2 text-right">{t('admin.aiBalance', 'Saldo')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {dados.accounts.map((c) => (
                    <tr
                      key={c.user_id || 'interno'}
                      className={`text-gray-900 dark:text-white ${c.user_id ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/60' : ''}`}
                      onClick={() => c.user_id && setConta({ id: c.user_id, label: [c.company, c.email].filter(Boolean).join(' · ') })}
                    >
                      <td className="py-2 pr-3">
                        <p className="font-medium">{c.user_id ? (c.company || c.name || c.email) : t('admin.aiInternal', 'Interno (sem conta)')}</p>
                        {c.user_id && <p className="text-xs text-gray-500 dark:text-gray-400">{c.email}</p>}
                      </td>
                      <td className="py-2 pr-3">{c.user_id ? (c.postpaid ? t('admin.aiPostpaid', 'Pós-paga') : t('admin.aiPrepaid', 'Pré-paga')) : '-'}</td>
                      <td className="py-2 pr-3 text-right">{formatarTokens(c.calls)}</td>
                      <td className="py-2 pr-3 text-right">{formatarTokens(c.input_tokens + c.output_tokens)}</td>
                      <td className="py-2 pr-3 text-right font-medium">{formatarReais(c.value_cents)}</td>
                      <td className={`py-2 text-right ${c.balance_cents != null && c.balance_cents < 0 ? 'text-red-600 dark:text-red-400' : ''}`}>
                        {c.balance_cents == null ? '-' : formatarReais(c.balance_cents)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <AiBillingConfigCard />

      <AiCreditsModal
        isOpen={!!conta}
        onClose={() => setConta(null)}
        userId={conta?.id || null}
        userLabel={conta?.label}
        onChanged={carregar}
      />
    </div>
  );
};

export default AdminAiCreditsPage;
